import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Save, X, Upload, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { fileToResizedDataUrl } from "@/lib/image";

const INPUT = "w-full px-3 py-2 rounded-lg border outline-none focus:ring-2 text-sm";
const BORDER = { borderColor: "#dcfce7" };
const CATEGORIES = ["Pendidikan", "Opini", "Kegiatan", "Pengumuman", "Umum"];

const emptyForm = { title: "", category: "Pendidikan", cover_image: "", excerpt: "", content: "", author: "Admin", published: true };

export default function ArticlesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await api.get("/articles");
    setItems(res.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
  const startEdit = (a) => {
    setForm({ title: a.title, category: a.category, cover_image: a.cover_image || "", excerpt: a.excerpt, content: a.content, author: a.author, published: a.published });
    setEditingId(a.id);
    setShowForm(true);
  };

  const handleImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const dataUrl = await fileToResizedDataUrl(file, 1200, 0.82);
    setForm((f) => ({ ...f, cover_image: dataUrl }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) await api.put(`/articles/${editingId}`, form);
      else await api.post("/articles", form);
      setShowForm(false);
      setForm(emptyForm);
      setEditingId(null);
      await load();
    } catch (err) {
      alert("Gagal menyimpan artikel");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus artikel ini?")) return;
    await api.delete(`/articles/${id}`);
    await load();
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-900">Kelola Artikel ({items.length})</h2>
        {!showForm && (
          <button onClick={startNew} data-testid="article-add-btn"
            className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ backgroundColor: "#198530" }}>
            <Plus size={16} /> Tambah Artikel
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={save} data-testid="article-form" className="bg-white rounded-xl border p-5 mb-6 space-y-4" style={BORDER}>
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-800">{editingId ? "Edit Artikel" : "Artikel Baru"}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Judul</label>
            <input className={INPUT} style={BORDER} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="article-title" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Kategori</label>
              <select className={INPUT} style={BORDER} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Penulis</label>
              <input className={INPUT} style={BORDER} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Gambar Sampul</label>
            <div className="flex items-center gap-4">
              {form.cover_image ? (
                <img src={form.cover_image} alt="preview" className="w-28 h-20 object-cover rounded-lg border" style={BORDER} />
              ) : (
                <div className="w-28 h-20 rounded-lg border flex items-center justify-center text-gray-300" style={BORDER}><ImageIcon size={24} /></div>
              )}
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer" style={{ backgroundColor: "#eef9f0", color: "#198530" }}>
                <Upload size={16} /> Pilih Gambar
                <input type="file" accept="image/*" className="hidden" onChange={handleImage} data-testid="article-image" />
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Ringkasan</label>
            <textarea className={INPUT} style={BORDER} rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Isi Artikel</label>
            <textarea className={INPUT} style={BORDER} rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="article-content" required />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            Tampilkan ke publik (published)
          </label>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="article-save"
              className="inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-lg text-sm font-bold disabled:opacity-60" style={{ backgroundColor: "#198530" }}>
              <Save size={16} /> {saving ? "Menyimpan..." : "Simpan"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-lg text-sm font-semibold border" style={BORDER}>Batal</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} data-testid={`article-row-${a.id}`} className="bg-white rounded-xl border p-4 flex items-center gap-4" style={BORDER}>
            <img src={a.cover_image || "https://via.placeholder.com/120x80?text=No+Image"} alt={a.title} className="w-24 h-16 object-cover rounded-lg flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: "#eef9f0", color: "#198530" }}>{a.category}</span>
                {!a.published && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Draft</span>}
              </div>
              <p className="font-semibold text-gray-900 text-sm truncate">{a.title}</p>
              <p className="text-xs text-gray-400">{new Date(a.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })} • {a.author}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => startEdit(a)} className="p-2 rounded-lg" style={{ backgroundColor: "#eef9f0", color: "#198530" }}><Edit2 size={16} /></button>
              <button onClick={() => remove(a.id)} data-testid={`article-delete-${a.id}`} className="p-2 rounded-lg bg-red-50 text-red-600"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-gray-400 py-10">Belum ada artikel.</p>}
      </div>
    </div>
  );
}
